import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiTrendingUp, FiMail, FiArrowLeft, FiCheckCircle, FiLock, FiKey } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const forgotPasswordMutation = useApi.useForgotPassword({
    onSuccess: (data) => {
      setSuccessMessage('OTP sent successfully! Please check your email.');
      setStep('verify');
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    },
  });

  const resetPasswordMutation = useApi.useResetPassword({
    onSuccess: (data) => {
      setSuccessMessage('Password reset successfully! Logging you in...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to reset password. Please check your OTP and try again.');
    },
  });

  const handleRequestOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    forgotPasswordMutation.mutate(email);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!otp.trim()) {
      setError('OTP is required');
      return;
    }
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    resetPasswordMutation.mutate({
      email,
      otp,
      password: newPassword
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-primary-100 to-yellow-100 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-primary-500 p-4 rounded-2xl shadow-lg">
              <FiTrendingUp className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-black">Trade Pilot</h2>
          <p className="mt-2 text-base sm:text-lg text-gray-600">
            {step === 'request' ? 'Reset Your Password' : 'Verify & Set Password'}
          </p>
          <p className="mt-1 text-sm text-gray-500 break-anywhere">
            {step === 'request'
              ? 'Enter your email to receive a 6-digit OTP'
              : `Enter the OTP sent to ${email} and your new password`
            }
          </p>
        </div>

        <div className="card">
          {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-4" />}
          {successMessage && (
            <div className="mb-4 p-4 rounded-md bg-green-50 text-green-700 flex items-center gap-3">
              <FiCheckCircle className="h-5 w-5 flex-shrink-0" />
              <p className="min-w-0 break-words">{successMessage}</p>
            </div>
          )}

          {step === 'request' ? (
            <form onSubmit={handleRequestOtp} className="space-y-6">
              <SimpleFormField
                label="Email Address"
                name="email"
                type="email"
                icon={<FiMail className="h-5 w-5 text-gray-400" />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
              />

              <button
                type="submit"
                disabled={forgotPasswordMutation.isPending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {forgotPasswordMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  <>
                    <FiMail className="h-5 w-5" />
                    <span>Send OTP</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <SimpleFormField
                label="Enter OTP Code"
                name="otp"
                type="text"
                icon={<FiKey className="h-5 w-5 text-gray-400" />}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                placeholder="123456"
                maxLength={6}
              />

              <SimpleFormField
                label="New Password"
                name="newPassword"
                type="password"
                icon={<FiLock className="h-5 w-5 text-gray-400" />}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Minimum 6 characters"
              />

              <SimpleFormField
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                icon={<FiLock className="h-5 w-5 text-gray-400" />}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
              />

              <button
                type="submit"
                disabled={resetPasswordMutation.isPending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {resetPasswordMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    <span>Resetting...</span>
                  </>
                ) : (
                  <>
                    <FiCheckCircle className="h-5 w-5" />
                    <span>Reset Password</span>
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep('request')}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Change Email / Resend OTP
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ForgotPassword;
